import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Meta from 'src/components/Meta';
import { Divider, PageCard, PageChartSkeleton, PageContainer, Spacer } from 'src/common';
import ScenariosCompare from './comps/scenarios/ScenariosCompare';
import { useGaEventPageView } from 'src/utils/gaEvent';
import { Charts } from './comps/charts';
import { AssessmentVersion } from 'src/api/types';
import useVersions from '../useVersions';
import styled from 'styled-components';
import { useComparisonAssessmentQuery } from 'src/graphql/_generated';

const Card = styled(PageCard)`
  padding: 0;
  padding: 20px 0px 30px 0px;

  .paddings {
    padding: 0px 30px 0px 30px;
  }
`;

const CompareAssessment = () => {
  const { idA, idB } = useParams<{ idA: string; idB: string }>();
  const { versionAId, versionBId } = useVersions();
  const [versionA, setVersionA] = useState<AssessmentVersion | undefined>(undefined);
  const [versionB, setVersionB] = useState<AssessmentVersion | undefined>(undefined);
  const [tab, setTab] = useState<0 | 1 | 2>(0);

  const { data: compareData, loading: isLoading } = useComparisonAssessmentQuery({
    variables: {
      assessmentAId: idA!,
      assessmentBId: idB!,
      versionAId,
      versionBId,
    },
  });

  useEffect(() => {
    if (compareData?.assessmentA?.versions) {
      const version = compareData.assessmentA.versions.find((v) => v.id === versionAId);
      setVersionA(version);
    }
    if (compareData?.assessmentB?.versions) {
      const version = compareData.assessmentB.versions.find((v) => v.id === versionBId);
      setVersionB(version);
    }
  }, [versionAId, versionBId, compareData]);

  useGaEventPageView('Cydea | Compare Assessments');

  if (isLoading || !compareData?.assessmentA || !compareData?.assessmentB)
    return (
      <PageContainer>
        <PageCard>
          <PageChartSkeleton />
        </PageCard>
      </PageContainer>
    );

  const showProjectedA = !!compareData.assessmentA.scenarios
    ?.flatMap((scenario) =>
      scenario.riskManagementPlans
        ?.filter((plan) => plan.control && plan.control.status !== 'Abandoned' && plan.control.status !== 'Retired')
        ?.flatMap((plan) => plan.modifiers),
    )
    ?.filter((modifier) => modifier.type !== 'NOTE_TYPE')?.length;

  const showProjectedB = !!compareData.assessmentB.scenarios
    ?.flatMap((scenario) =>
      scenario.riskManagementPlans
        ?.filter((plan) => plan.control && plan.control.status !== 'Abandoned' && plan.control.status !== 'Retired')
        ?.flatMap((plan) => plan.modifiers),
    )
    ?.filter((modifier) => modifier.type !== 'NOTE_TYPE')?.length;

  return (
    <>
      <Meta title={`Cydea | Compare Assessments`} feedbackScript />
      <PageContainer data-cy="compare-assessments-page">
        <Card>
          <Charts
            dataA={compareData.assessmentA}
            dataB={compareData.assessmentB}
            versionA={versionA}
            versionB={versionB}
            assessmentA={{
              seriesEnabled: tab === 0 || tab === 1,
              showProjected: showProjectedA,
            }}
            assessmentB={{
              seriesEnabled: tab === 0 || tab === 2,
              showProjected: showProjectedB,
            }}
          />

          <Spacer $px={20} />
          <Divider />
          <Spacer $px={30} />
          <div className="paddings">
            <ScenariosCompare
              tab={tab}
              setTab={setTab}
              dataA={compareData.assessmentA}
              dataB={compareData.assessmentB}
              versionA={versionA}
              versionB={versionB}
            />
          </div>
        </Card>
      </PageContainer>
    </>
  );
};

export default CompareAssessment;
